import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from "react";
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ContactPhoneOutlinedIcon from "@mui/icons-material/ContactPhoneOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { setMessage, useMaterialUIController } from "context";
import { phoneApplyMask } from "helpers/utils";
import type { ILojaConfig } from "interfaces/loja";
import { createOrUpdateLojaConfig, getLojaConfig } from "services/lojaConfig";
import { getCep } from "services/uteis";
import { LojaPageContainer } from "../common";
import ConfigFormSection from "./components/ConfigFormSection";
import {
  CONFIG_ACCENT,
  infoBannerSx,
  saveButtonSx,
  stickyFooterSx,
  switchSx,
} from "./configuracoes.theme";
import {
  EMPTY_LOJA_CONFIG,
  applyLoadedMasks,
  buildSavePayload,
  computeConfigStats,
  formatCEP,
  formatCNPJInput,
  getFieldErrors,
  validateConfig,
} from "./components/config.helpers";

function StatMini({
  label,
  value,
  accent,
  ok,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
  ok?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography
        variant="h4"
        fontWeight="bold"
        color={
          ok === true
            ? "success.dark"
            : ok === false
              ? "warning.dark"
              : accent
                ? CONFIG_ACCENT
                : "text.primary"
        }
        sx={{ fontSize: { xs: "1.1rem", sm: "1.75rem" } }}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default function LojaConfig() {
  const [, dispatch] = useMaterialUIController();
  const [config, setConfig] = useState<ILojaConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, status } = await getLojaConfig();
      if (status === 200 && data) {
        setConfig(applyLoadedMasks(data));
      } else {
        setConfig({ ...EMPTY_LOJA_CONFIG });
      }
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : "Falha ao carregar configuração da loja.";
      setMessage(dispatch, { open: true, severity: "error", text: msg });
      setConfig({ ...EMPTY_LOJA_CONFIG });
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    void load();
  }, [load]);

  function updateField<K extends keyof ILojaConfig>(key: K, value: ILojaConfig[K]) {
    setConfig((c) => ({ ...(c ?? EMPTY_LOJA_CONFIG), [key]: value }) as ILojaConfig);
  }

  async function handleCepChange(value: string) {
    const formattedCep = formatCEP(value);
    updateField("cep", formattedCep);

    const cepDigits = formattedCep.replace(/\D/g, "");
    if (cepDigits.length !== 8) return;

    setLoadingCep(true);
    try {
      const cepData = await getCep(cepDigits);
      if (cepData.erro) {
        setMessage(dispatch, {
          open: true,
          severity: "warning",
          text: `CEP '${formattedCep}' não encontrado.`,
        });
      } else {
        if (cepData.logradouro) updateField("logradouro", cepData.logradouro);
        if (cepData.bairro) updateField("bairro", cepData.bairro);
        if (cepData.localidade) updateField("cidade", cepData.localidade);
        if (cepData.uf) updateField("uf", cepData.uf.toUpperCase());
        if (cepData.cep) updateField("cep", formatCEP(cepData.cep));
      }
    } catch {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: "Erro ao buscar CEP. Tente novamente.",
      });
    } finally {
      setLoadingCep(false);
    }
  }

  const stats = useMemo(() => computeConfigStats(config), [config]);
  const estadoErros = useMemo(() => getFieldErrors(config), [config]);

  const onSave = useCallback(async () => {
    if (!config) return;
    const v = validateConfig(config);
    if (v.ok === false) {
      setMessage(dispatch, { open: true, severity: "error", text: v.msg });
      return;
    }

    setSaving(true);
    try {
      await createOrUpdateLojaConfig(buildSavePayload(config));
      setMessage(dispatch, {
        open: true,
        severity: "success",
        text: "Configurações da empresa atualizadas com sucesso.",
      });
      await load();
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { message?: string; error?: string; errors?: string[] } };
        message?: string;
      };
      const serverMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        (Array.isArray(err?.response?.data?.errors) &&
          err.response.data.errors.join(", ")) ||
        err?.message;
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: serverMsg || "Falha ao salvar configurações da empresa.",
      });
    } finally {
      setSaving(false);
    }
  }, [config, dispatch, load]);

  if (loading) {
    return (
      <LojaPageContainer title="Dados da empresa" subtitle="Carregando configurações…">
        <MDBox py={8} textAlign="center">
          <CircularProgress sx={{ color: CONFIG_ACCENT }} />
        </MDBox>
      </LojaPageContainer>
    );
  }

  return (
    <LojaPageContainer
      title="Dados da empresa"
      subtitle="Informações fiscais, endereço e regras operacionais usadas na loja e no Melhor Envio."
      actions={
        <MDButton
          variant="gradient"
          color="dark"
          size="small"
          startIcon={<SaveOutlinedIcon />}
          onClick={() => void onSave()}
          disabled={saving}
          sx={saveButtonSx}
        >
          {saving ? "Salvando…" : "Salvar"}
        </MDButton>
      }
    >
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <StatMini label="Dados da empresa" value={`${stats.empresaPct}%`} accent />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini
            label="Endereço completo"
            value={stats.enderecoOk ? "Sim" : "Pendente"}
            ok={stats.enderecoOk}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini
            label="Contato preenchido"
            value={stats.contatoOk ? "Sim" : "Pendente"}
            ok={stats.contatoOk}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini
            label="Devolver estoque"
            value={stats.estoqueAuto ? "Ativo" : "Inativo"}
          />
        </Grid>
      </Grid>

      <Stack spacing={3}>
        <ConfigFormSection
          title="Dados da empresa"
          subtitle="Identificação fiscal exibida em documentos e integrações."
          icon={<BusinessOutlinedIcon />}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <MDInput
                label="Nome da loja"
                value={config?.nomeLoja || ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  updateField("nomeLoja", e.target.value)
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MDInput
                label="CNPJ"
                value={config?.cnpj || ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  updateField("cnpj", formatCNPJInput(e.target.value))
                }
                placeholder="00.000.000/0000-00"
                error={estadoErros.cnpjInvalido}
                helperText={
                  estadoErros.cnpjInvalido ? "CNPJ deve conter 14 dígitos" : undefined
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MDInput
                label="Razão social"
                value={config?.razaoSocial || ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  updateField("razaoSocial", e.target.value)
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MDInput
                label="Nome fantasia"
                value={config?.nomeFantasia || ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  updateField("nomeFantasia", e.target.value)
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MDInput
                label="Inscrição estadual"
                value={config?.inscricaoEstadual || ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  updateField("inscricaoEstadual", e.target.value)
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MDInput
                label="Inscrição municipal"
                value={config?.inscricaoMunicipal || ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  updateField("inscricaoMunicipal", e.target.value)
                }
                fullWidth
              />
            </Grid>
          </Grid>
        </ConfigFormSection>

        <ConfigFormSection
          title="Contato"
          subtitle="Telefone e e-mail usados como remetente nas etiquetas do Melhor Envio."
          icon={<ContactPhoneOutlinedIcon />}
        >
          <MDBox sx={{ ...infoBannerSx, mb: 3 }}>
            <MDTypography variant="caption" color="text.secondary">
              Preencha estes campos antes de configurar frete em{" "}
              <strong>Configurações → Frete Melhor Envio</strong>.
            </MDTypography>
          </MDBox>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <MDInput
                label="Telefone de contato"
                value={phoneApplyMask(config?.phoneContato || "")}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  updateField("phoneContato", e.target.value)
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MDInput
                type="email"
                label="E-mail de contato"
                value={config?.emailContato || ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  updateField("emailContato", e.target.value)
                }
                fullWidth
              />
            </Grid>
          </Grid>
        </ConfigFormSection>

        <ConfigFormSection
          title="Endereço"
          subtitle="Endereço de origem para expedição e cálculo de frete."
          icon={<HomeOutlinedIcon />}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <MDInput
                label="CEP"
                value={config?.cep || ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  void handleCepChange(e.target.value);
                }}
                placeholder="00000-000"
                error={estadoErros.cepInvalido}
                helperText={
                  loadingCep
                    ? "Buscando endereço…"
                    : estadoErros.cepInvalido
                      ? "CEP inválido"
                      : "Busca automática ao completar 8 dígitos"
                }
                disabled={loadingCep}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MDInput
                label="Logradouro"
                value={config?.logradouro || ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  updateField("logradouro", e.target.value)
                }
                placeholder="Rua, avenida…"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <MDInput
                label="Número"
                value={config?.numero || ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  updateField("numero", e.target.value)
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MDInput
                label="Complemento"
                value={config?.complemento || ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  updateField("complemento", e.target.value)
                }
                placeholder="Apto, bloco…"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MDInput
                label="Bairro"
                value={config?.bairro || ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  updateField("bairro", e.target.value)
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MDInput
                label="Cidade"
                value={config?.cidade || ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  updateField("cidade", e.target.value)
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <MDInput
                label="UF"
                value={config?.uf || ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  updateField("uf", e.target.value.toUpperCase().slice(0, 2))
                }
                placeholder="SP"
                inputProps={{ maxLength: 2 }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <MDInput
                label="País"
                value={config?.pais || ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  updateField("pais", e.target.value.toUpperCase().slice(0, 2))
                }
                placeholder="BR"
                inputProps={{ maxLength: 2 }}
                fullWidth
              />
            </Grid>
          </Grid>
        </ConfigFormSection>

        <ConfigFormSection
          title="Operacional"
          subtitle="Regras automáticas de estoque na loja."
          icon={<Inventory2OutlinedIcon />}
        >
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(config?.retornarEstoqueCancelado)}
                onChange={(e) => updateField("retornarEstoqueCancelado", e.target.checked)}
                sx={switchSx}
              />
            }
            label={
              <Box>
                <MDTypography variant="body2" fontWeight="medium">
                  Retornar estoque ao cancelar pedido
                </MDTypography>
                <MDTypography variant="caption" color="text.secondary">
                  Quando ativo, itens voltam ao estoque automaticamente após cancelamento.
                </MDTypography>
              </Box>
            }
          />
        </ConfigFormSection>

        <MDBox sx={stickyFooterSx}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SettingsOutlinedIcon sx={{ color: CONFIG_ACCENT }} />
            <MDTypography variant="button" color="text">
              Revise os dados antes de salvar — usados em frete e operações da loja.
            </MDTypography>
          </Stack>
          <MDButton
            variant="gradient"
            color="dark"
            startIcon={saving ? undefined : <SaveOutlinedIcon />}
            onClick={() => void onSave()}
            disabled={saving}
            sx={saveButtonSx}
          >
            {saving ? "Salvando…" : "Salvar configurações"}
          </MDButton>
        </MDBox>
      </Stack>
    </LojaPageContainer>
  );
}
